<template>
  <div>
    <themed-form
      :model-value="form"
      :fields="formFields"
      :errors="formErrors"
      :loading="loading"
      layout="grid"
      :columns="12"
      @input="handleFormInput"
      @submit="submit"
    >
      <template #submit="{ loading }">
        <div class="flex justify-end mt-6">
          <themed-button
            type="submit"
            variant="default"
            :disabled="loading"
          >
            <span v-if="loading">Please wait...</span>
            <span v-else>{{ client && user ? 'Update and Continue' : 'Continue' }}</span>
          </themed-button>
        </div>
      </template>
    </themed-form>
  </div>
</template>

<script>
import ThemedForm from '../form/ThemedForm.vue';
import ThemedButton from '../ThemedButton.vue';
import axios from 'axios';
import { formatCellNumber } from '../../../../utils/formatters';

export default {
  name: 'ClientSetupForm',
  components: { 
    ThemedForm,
    ThemedButton
  },
  props: {
    formData: Object,
    client: Object,
    user: Object,
  },
  data() {
    return {
      loading: false,
      form: {
        name: '',
        email: '',
        address: '',
        city: '',
        state: '',
        postal_code: '',
        country_id: 16,
        password: '',
        password_confirmation: '',
        cell: '',
      },
      formErrors: {},
      countries: [],
      states: [],
    }
  },
  computed: {
    formFields() {
      const isUpdate = !!(this.client && this.user);
      
      return [
        {
          name: 'name',
          type: 'input',
          label: 'Name',
          required: true,
          col: 4,
          validation: {
            required: true
          }
        },
        {
          name: 'email',
          type: 'input',
          inputType: 'email',
          label: 'Email',
          required: true,
          componentProps: {
            readonly: isUpdate
          },
          col: 4,
          validation: {
            required: true,
            email: true
          }
        },
        {
          name: 'cell',
          type: 'input',
          label: 'Cell',
          required: true,
          tooltip: 'Format: +1-555-555-5555',
          col: 4,
          validation: {
            required: true,
            pattern: /^\+1[-.\s]?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/,
            message: 'Enter cell with country code (e.g., +1-555-555-5555).'
          }
        },
        {
          name: 'address',
          type: 'input',
          label: 'Address',
          required: true,
          col: 12,
          validation: {
            required: true
          }
        },
        {
          name: 'city',
          type: 'input',
          label: 'City',
          required: true,
          col: 4,
          validation: {
            required: true
          }
        },
        {
          name: 'state',
          type: 'select',
          label: 'State',
          required: true,
          options: this.stateOptions,
          placeholder: 'Select state',
          col: 4,
          validation: {
            required: true
          }
        },
        {
          name: 'postal_code',
          type: 'input',
          label: 'Postal Code',
          required: true,
          componentProps: {
            maxlength: 15
          },
          col: 4,
          validation: {
            required: true
          }
        },
        {
          name: 'country_id',
          type: 'select',
          label: 'Country',
          required: true,
          options: this.countryOptions,
          placeholder: 'Select country',
          col: 12,
          validation: {
            required: true
          }
        },
        // Password fields - only show for new signups
        {
          name: 'password',
          type: 'input',
          inputType: 'password',
          label: 'Password',
          required: true,
          conditionalFn: () => !isUpdate,
          col: 6,
          validation: {
            required: !isUpdate,
            minLength: 6,
            message: 'Password must be at least 6 characters.'
          }
        },
        {
          name: 'password_confirmation',
          type: 'input',
          inputType: 'password',
          label: 'Confirm Password',
          required: true,
          conditionalFn: () => !isUpdate,
          col: 6,
          validation: {
            required: !isUpdate,
            matchField: 'password',
            message: 'Passwords do not match.'
          }
        }
      ];
    },
    
    countryOptions() {
      return this.countries.map(country => ({
        value: country.id,
        label: country.name
      }));
    },
    
    stateOptions() {
      return this.states.map(state => ({
        value: state.state,
        label: state.state
      }));
    }
  },
  mounted() {
    this.loadAddressInfo();
    this.initializeForm();
  },
  methods: {
    handleFormInput(newFormData) {
      // Update form data - validation is handled by ThemedForm
      this.form = newFormData;
    },
    
    async loadAddressInfo() {
      try {
        const response = await axios.get('/api/address-info');
        this.countries = response.data.countries;
        this.states = response.data.states;
      } catch (error) {
        console.error('Failed to load address info:', error);
      }
    },
    
    initializeForm() {
      if (this.formData) {
        this.form = { ...this.form, ...this.formData };
      } else if (this.client && this.user) {
        this.form = {
          ...this.form,
          name: this.user.name,
          email: this.user.email,
          address: this.client.address,
          city: this.client.city,
          state: this.client.state,
          postal_code: this.client.postal_code,
          country_id: this.client.country_id,
          cell: formatCellNumber(this.user.cell)
        };
      }
    },
    
    async submit() {
      this.formErrors = {};
      this.loading = true;
      
      const isUpdate = !!(this.client && this.user);
      const payload = {
        client: {
          name: this.form.name,
          address: this.form.address,
          city: this.form.city,
          state: this.form.state,
          postal_code: this.form.postal_code,
          country_id: this.form.country_id
        },
        user: {
          name: this.form.name,
          email: this.form.email,
          cell: this.form.cell,
          country_id: this.form.country_id
        }
      };
      
      if (isUpdate) {
        payload.client_id = this.client.id;
        payload.user_id = this.user.id;
      } else {
        payload.user.password = this.form.password;
        payload.user.password_confirmation = this.form.password_confirmation;
      }
      
      try {
        const url = isUpdate ? '/signup/update' : '/signup';
        const resp = await axios.post(url, payload, { validateStatus: () => true });
        
        if (resp.data.errors) {
          this.formErrors = resp.data.errors;
          
          if (resp.data.errors['user.email']) {
            this.$notify({
              type: 'error',
              title: 'Email Exists',
              text: 'This email is already registered. Please log in or use another email.',
              duration: 2000
            });
          } else {
            this.$notify({
              type: 'error',
              title: 'Error',
              text: this.getFirstErrorMessage(resp.data.errors)
            });
          }
          
          this.loading = false;
          return;
        }
        
        if (resp.data.client && resp.data.user) {
          this.$notify({
            type: 'success',
            title: 'Success',
            text: resp.data.message || (isUpdate ? 'Update successful!' : 'Signup successful!')
          });
          this.$emit('signup-setup-complete', { 
            client: resp.data.client, 
            user: resp.data.user, 
            formData: { ...this.form } 
          });
        } else if (resp.data.message && resp.status !== 201) {
          this.$notify({
            type: 'error',
            title: 'Error',
            text: resp.data.message
          });
          this.loading = false;
        }
      } catch (error) {
        this.formErrors = error.response?.data?.errors || {};
        this.$notify({
          type: 'error',
          title: 'Error',
          text: this.getFirstErrorMessage(this.formErrors, 'An unexpected error occurred.')
        });
        this.loading = false;
      }
    },
    
    getFirstErrorMessage(errorsObj, fallback = 'Please check the form for errors.') {
      for (const key in errorsObj) {
        if (Array.isArray(errorsObj[key]) && errorsObj[key].length > 0) {
          return errorsObj[key][0];
        }
        if (typeof errorsObj[key] === 'object' && errorsObj[key] !== null) {
          const nested = this.getFirstErrorMessage(errorsObj[key]);
          if (nested) return nested;
        }
      }
      return fallback;
    }
  }
}
</script>
